import { useState, useEffect } from 'react';
import axios from 'axios';
import { useInactiveLabels } from '../hooks/useInactiveLabels';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  TrendingUp, BarChart3, Activity, Users, Building2, AlertTriangle, X, Plus, History
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6'];

function MiniBar({ value, max, color = '#3b82f6', height = 20 }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="w-full bg-muted rounded" style={{ height }}>
      <div className="rounded transition-all" style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: color }} />
    </div>
  );
}

export default function AnalyticsDashboard() {
  const withInactive = useInactiveLabels();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Agency comparison
  const [availableAgencies, setAvailableAgencies] = useState([]);
  const [selectedAgencies, setSelectedAgencies] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);
  const [agencySearch, setAgencySearch] = useState('');
  const [showAgencyDropdown, setShowAgencyDropdown] = useState(false);
  // Agency monthly line graph
  const [agencyMonthly, setAgencyMonthly] = useState(null);
  // Sparkline filters
  const [includeHistorical, setIncludeHistorical] = useState(false);
  const [stateFilter, setStateFilter] = useState('');
  const [hoverTooltip, setHoverTooltip] = useState(null); // {x, y, label, value}

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        const monthlyUrl = `${API}/analytics/agency-monthly?include_historical=${includeHistorical}${stateFilter ? `&state=${stateFilter}` : ''}`;
        const [dashRes, agencyRes, monthlyRes] = await Promise.all([
          axios.get(`${API}/analytics/dashboard`, { headers }),
          axios.get(`${API}/analytics/agency-comparison`, { headers }),
          axios.get(monthlyUrl, { headers }),
        ]);
        setData(dashRes.data);
        setAvailableAgencies(agencyRes.data.agencies_available || []);
        setAgencyMonthly(monthlyRes.data);
      } catch (err) {
        console.error('Analytics fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [includeHistorical, stateFilter]);

  const fetchComparison = async (agencies) => {
    if (agencies.length === 0) { setComparisonData(null); return; }
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/analytics/agency-comparison?agencies=${agencies.join(',')}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComparisonData(res.data);
    } catch (err) {
      console.error('Comparison fetch error:', err);
    }
  };

  const addAgency = (name) => {
    if (selectedAgencies.length >= 5 || selectedAgencies.includes(name)) return;
    const next = [...selectedAgencies, name];
    setSelectedAgencies(next);
    setAgencySearch('');
    setShowAgencyDropdown(false);
    fetchComparison(next);
  };

  const removeAgency = (name) => {
    const next = selectedAgencies.filter(a => a !== name);
    setSelectedAgencies(next);
    fetchComparison(next);
  };

  const filteredAgencies = availableAgencies
    .filter(a => a.name.toLowerCase().includes(agencySearch.toLowerCase()))
    .filter(a => !selectedAgencies.includes(a.name))
    .slice(0, 8);

  if (loading) return <div className="p-12 text-center text-muted-foreground">Loading analytics...</div>;
  if (!data) return <div className="p-12 text-center text-muted-foreground">No data available</div>;

  const { visit_trend, mv_trend, top_agencies, top_therapists } = data;
  const maxVisits = Math.max(...visit_trend.map(w => w.visits), 1);
  const maxMv = Math.max(...mv_trend.map(w => w.mv), 1);
  const maxAgencyVisits = Math.max(...top_agencies.map(a => a.visits), 1);
  const maxTherapistVisits = Math.max(...top_therapists.map(t => t.visits), 1);

  // Comparison chart max
  const compMax = comparisonData ? Math.max(...comparisonData.comparison.flatMap(c => c.weeks.map(w => w.visits)), 1) : 1;

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 text-center">
          <BarChart3 size={22} className="mx-auto mb-1 text-primary" />
          <p className="text-2xl font-bold">{data.total_visits.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground">Total Weighted Visits</p>
        </Card>
        <Card className="p-4 text-center">
          <Activity size={22} className="mx-auto mb-1 text-primary" />
          <p className="text-2xl font-bold">{data.total_weeks}</p>
          <p className="text-xs text-muted-foreground">Weeks of Data</p>
        </Card>
        <Card className="p-4 text-center">
          <Building2 size={22} className="mx-auto mb-1 text-primary" />
          <p className="text-2xl font-bold">{top_agencies.length}</p>
          <p className="text-xs text-muted-foreground">Top Agencies</p>
        </Card>
        <Card className="p-4 text-center">
          <Users size={22} className="mx-auto mb-1 text-primary" />
          <p className="text-2xl font-bold">{top_therapists.length}</p>
          <p className="text-xs text-muted-foreground">Top Therapists</p>
        </Card>
      </div>

      {/* Agency Comparison */}
      <Card className="p-5" data-testid="agency-comparison-section">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Building2 size={18} /> Agency Comparison (select up to 5)
        </h3>
        
        {/* Agency Selector */}
        <div className="flex flex-wrap gap-2 mb-3">
          {selectedAgencies.map((name, i) => (
            <Badge key={name} className="px-2 py-1 gap-1 text-white" style={{ background: COLORS[i % COLORS.length] }}>
              {name}
              <X size={12} className="cursor-pointer" onClick={() => removeAgency(name)} />
            </Badge>
          ))}
          {selectedAgencies.length < 5 && (
            <div className="relative">
              <div className="flex items-center gap-1">
                <Input
                  placeholder="Search agency..."
                  value={agencySearch}
                  onChange={e => { setAgencySearch(e.target.value); setShowAgencyDropdown(true); }}
                  onFocus={() => setShowAgencyDropdown(true)}
                  className="w-48 h-8 text-sm"
                  data-testid="agency-search-input"
                />
                <Plus size={16} className="text-muted-foreground" />
              </div>
              {showAgencyDropdown && agencySearch && filteredAgencies.length > 0 && (
                <div className="absolute top-9 left-0 bg-background border rounded-md shadow-lg z-50 w-64 max-h-48 overflow-y-auto">
                  {filteredAgencies.map(a => (
                    <div key={a.name} className="px-3 py-2 hover:bg-muted cursor-pointer text-sm flex justify-between"
                      onClick={() => addAgency(a.name)} data-testid={`agency-option-${a.name}`}>
                      <span>{a.name}</span>
                      <Badge className="text-[10px] bg-muted">{a.state}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Comparison Chart */}
        {comparisonData && comparisonData.comparison.length > 0 ? (
          <div>
            <div className="flex items-end gap-1 h-48">
              {comparisonData.weeks.map((wk, wi) => (
                <div key={wi} className="flex-1 flex flex-col items-center justify-end h-full gap-0.5">
                  <div className="w-full flex gap-px justify-center">
                    {comparisonData.comparison.map((agency, ai) => {
                      const weekData = agency.weeks[wi];
                      const h = weekData ? (weekData.visits / compMax) * 100 : 0;
                      return (
                        <div key={ai} className="flex-1 rounded-t transition-all"
                          style={{ height: `${Math.max(h, 2)}%`, background: COLORS[ai % COLORS.length], maxWidth: '20px' }}
                          title={`${agency.agency}: ${weekData?.visits || 0}`} />
                      );
                    })}
                  </div>
                  <span className="text-[9px] text-muted-foreground mt-1">{wk.slice(5)}</span>
                </div>
              ))}
            </div>
            {/* Legend */}
            <div className="flex gap-4 mt-3 justify-center flex-wrap">
              {comparisonData.comparison.map((c, i) => (
                <span key={i} className="flex items-center gap-1 text-xs">
                  <span className="w-3 h-3 rounded" style={{ background: COLORS[i % COLORS.length] }} />
                  {c.agency}
                </span>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-center text-muted-foreground text-sm py-8">
            Select agencies above to compare weekly visit trends side-by-side
          </p>
        )}
      </Card>

      {/* Visit Trend Chart */}
      <Card className="p-5">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <TrendingUp size={18} /> Visit Trend (Last 12 Weeks)
        </h3>
        <div className="flex items-end gap-1 h-48">
          {visit_trend.map((w, i) => {
            const h = (w.visits / maxVisits) * 100;
            return (
              <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
                <span className="text-xs font-medium mb-1">{w.visits}</span>
                <div className="w-full rounded-t bg-primary/80 transition-all hover:bg-primary" style={{ height: `${h}%`, minHeight: '4px' }} />
                <span className="text-[10px] text-muted-foreground mt-1 rotate-[-45deg] origin-top-left w-10 truncate">{w.week.slice(5)}</span>
              </div>
            );
          })}
        </div>
      </Card>

      {/* MV Trend Chart */}
      <Card className="p-5">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <AlertTriangle size={18} className="text-amber-500" /> Missed Visit Trend
        </h3>
        <div className="flex items-end gap-1 h-36">
          {mv_trend.map((w, i) => {
            const h = maxMv > 0 ? (w.mv / maxMv) * 100 : 2;
            return (
              <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
                <span className="text-xs font-medium mb-1">{w.mv > 0 ? w.mv : ''}</span>
                <div className="w-full rounded-t bg-amber-400 transition-all hover:bg-amber-500" style={{ height: `${h}%`, minHeight: w.mv > 0 ? '4px' : '2px' }} />
                <span className="text-[10px] text-muted-foreground mt-1">{w.pct > 0 ? `${w.pct}%` : ''}</span>
              </div>
            );
          })}
        </div>
      </Card>


      {/* Agency Weekly Sparkline Grid */}
      {agencyMonthly && (() => {
        const { agencies: agList, weeks: wks, states: distinctStates = [] } = agencyMonthly;
        const rowH = 48;
        const sparkW = 200;
        const sparkH = 36;

        const formatWeek = (iso) => {
          const d = new Date(iso + 'T00:00:00');
          return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
        };

        return (
          <Card className="p-5" data-testid="agency-monthly-chart">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h3 className="font-semibold flex items-center gap-2">
                <Building2 size={18} /> All Agencies — Weekly Visits (Rolling 12 Months)
              </h3>
              <div className="flex items-center gap-3 flex-wrap">
                <label className="flex items-center gap-2 text-xs cursor-pointer select-none" data-testid="include-historical-toggle">
                  <input
                    type="checkbox"
                    checked={includeHistorical}
                    onChange={(e) => setIncludeHistorical(e.target.checked)}
                    className="h-3.5 w-3.5"
                  />
                  <History size={12} /> Include historical totals
                </label>
                <select
                  value={stateFilter}
                  onChange={(e) => setStateFilter(e.target.value)}
                  className="text-xs border rounded px-2 py-1 bg-background"
                  data-testid="sparkline-state-filter"
                >
                  <option value="">All states</option>
                  {distinctStates.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            {agList.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">No agencies match the current filters.</p>
            ) : (
              <div className="max-h-[700px] overflow-y-auto relative">
                {agList.map((agency, ai) => {
                  const localMax = Math.max(...agency.weeks, 1);
                  const points = agency.weeks.map((val, wi) => {
                    const x = (wi / Math.max(wks.length - 1, 1)) * sparkW;
                    const y = sparkH - (val / localMax) * (sparkH - 4) - 2;
                    return { x, y, val, week: wks[wi] };
                  });
                  const polyPoints = points.map(p => `${p.x},${p.y}`).join(' ');
                  const areaPoints = `0,${sparkH} ${polyPoints} ${sparkW},${sparkH}`;

                  return (
                    <div
                      key={ai}
                      className={`flex items-center cursor-pointer hover:bg-primary/5 transition-colors ${ai % 2 === 0 ? '' : 'bg-muted/30'}`}
                      style={{ height: `${rowH}px` }}
                      data-testid={`sparkline-row-${ai}`}
                      onClick={() => navigate(`/agencies?search=${encodeURIComponent(agency.agency)}`)}
                      title={`Click to view ${agency.agency}`}
                    >
                      <div className="w-56 flex-shrink-0 text-sm font-medium truncate pr-3 flex items-center gap-1">
                        <span className="truncate">{agency.agency}</span>
                        {agency.state && <Badge className="text-[9px] px-1 py-0 bg-muted text-muted-foreground">{agency.state}</Badge>}
                      </div>
                      <div className="flex-1 px-1 relative">
                        <svg
                          width="100%"
                          height={sparkH}
                          viewBox={`0 0 ${sparkW} ${sparkH}`}
                          preserveAspectRatio="none"
                          onMouseLeave={() => setHoverTooltip(null)}
                          onMouseMove={(e) => {
                            const svgRect = e.currentTarget.getBoundingClientRect();
                            const xRatio = (e.clientX - svgRect.left) / svgRect.width;
                            const idx = Math.min(Math.max(Math.round(xRatio * (wks.length - 1)), 0), wks.length - 1);
                            const p = points[idx];
                            setHoverTooltip({
                              x: e.clientX,
                              y: e.clientY,
                              label: formatWeek(p.week),
                              value: p.val,
                              agency: agency.agency,
                            });
                          }}
                        >
                          <polygon points={areaPoints} fill="#1a3a5c" opacity="0.06" />
                          <polyline points={polyPoints} fill="none" stroke="#1a3a5c" strokeWidth="1.5" strokeLinejoin="round" />
                        </svg>
                      </div>
                      <div className="w-16 flex-shrink-0 text-sm font-bold text-right tabular-nums">{agency.total}</div>
                    </div>
                  );
                })}
              </div>
            )}

            {hoverTooltip && (
              <div
                className="fixed z-50 bg-foreground text-background text-xs rounded-md px-2.5 py-1.5 shadow-lg pointer-events-none"
                style={{ left: hoverTooltip.x + 12, top: hoverTooltip.y - 10 }}
                data-testid="sparkline-tooltip"
              >
                <div className="font-semibold">{hoverTooltip.agency}</div>
                <div className="opacity-80">{hoverTooltip.label}</div>
                <div className="font-bold tabular-nums">{hoverTooltip.value} visit{hoverTooltip.value === 1 ? '' : 's'}</div>
              </div>
            )}
          </Card>
        );
      })()}


      {/* Two column: Agency Performance + Therapist Productivity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-5">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Building2 size={18} /> Agency Performance (Top 20)
          </h3>
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {top_agencies.map((a, i) => (
              <div key={i} className="flex items-center gap-3" data-testid={`agency-row-${i}`}>
                <span className="text-xs text-muted-foreground w-5 text-right">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-medium truncate">{a.agency}</span>
                    <Badge className="text-[10px] px-1.5 bg-muted text-muted-foreground">{a.state}</Badge>
                  </div>
                  <MiniBar value={a.visits} max={maxAgencyVisits} color="#1a3a5c" height={14} />
                </div>
                <div className="text-right w-20">
                  <p className="text-sm font-bold">{a.visits}</p>
                  <p className="text-[10px] text-muted-foreground">{a.avg_per_week}/wk</p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Users size={18} /> Therapist Productivity (Top 20)
          </h3>
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {top_therapists.map((t, i) => (
              <div key={i} className="flex items-center gap-3" data-testid={`therapist-row-${i}`}>
                <span className="text-xs text-muted-foreground w-5 text-right">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-medium truncate">{withInactive(t.name)}</span>
                    {t.mv > 0 && <Badge className="text-[10px] px-1.5 bg-amber-100 text-amber-700">{t.mv} MV</Badge>}
                  </div>
                  <MiniBar value={t.visits} max={maxTherapistVisits} color="#1e6b4a" height={14} />
                </div>
                <div className="text-right w-20">
                  <p className="text-sm font-bold">{t.visits}</p>
                  <p className="text-[10px] text-muted-foreground">{t.avg_per_week}/wk</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
